import React, { useState, useEffect } from 'react';
import adminApi from '../services/adminApi';

const CATEGORIES = ['all', 'limits', 'auth', 'ab_test'];
const TYPES = ['number', 'string', 'boolean', 'json'];

function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [cacheStats, setCacheStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    type: 'string',
    category: 'general',
    description: ''
  });

  useEffect(() => {
    loadSettings();
  }, [selectedCategory]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const category = selectedCategory === 'all' ? null : selectedCategory;
      const response = await adminApi.getSettings(category);
      setSettings(response.settings || []);
      setCacheStats(response.cacheStats);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshCache = async () => {
    try {
      await adminApi.refreshCache();
      await loadSettings();
      alert('Cache refreshed successfully');
    } catch (err) {
      alert('Failed to refresh cache: ' + err.message);
    }
  };

  const openCreateModal = () => {
    setEditingSetting(null);
    setFormData({
      key: '',
      value: '',
      type: 'string',
      category: 'general',
      description: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (setting) => {
    setEditingSetting(setting);
    setFormData({
      key: setting.key,
      value: setting.type === 'json' ? JSON.stringify(setting.value, null, 2) : String(setting.value),
      type: setting.type,
      category: setting.category,
      description: setting.description || ''
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      let value = formData.value;
      
      // Parse value based on type
      if (formData.type === 'number') {
        value = parseFloat(value);
        if (isNaN(value)) {
          alert('Invalid number');
          return;
        }
      } else if (formData.type === 'boolean') {
        value = value === 'true' || value === true;
      } else if (formData.type === 'json') {
        try {
          value = JSON.parse(value);
        } catch {
          alert('Invalid JSON');
          return;
        }
      }

      const data = {
        ...formData,
        value
      };

      if (editingSetting) {
        await adminApi.updateSetting(editingSetting.key, data);
      } else {
        await adminApi.createSetting(data);
      }

      setIsModalOpen(false);
      await loadSettings();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Are you sure you want to delete "${key}"?`)) {
      return;
    }

    try {
      await adminApi.deleteSetting(key);
      await loadSettings();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const formatValue = (setting) => {
    if (setting.type === 'boolean') {
      return setting.value ? '✅ true' : '❌ false';
    }
    if (setting.type === 'json') {
      return JSON.stringify(setting.value).slice(0, 50) + '...';
    }
    return String(setting.value);
  };

  const getCategoryBadge = (category) => {
    const colors = {
      limits: 'admin-badge-info',
      auth: 'admin-badge-warning',
      feature_flags: 'admin-badge-success',
      general: 'admin-badge-info',
      ab_test: 'admin-badge-danger'
    };
    return colors[category] || 'admin-badge-info';
  };

  if (isLoading && settings.length === 0) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1>Global Settings</h1>
        <p>Manage application configuration and feature flags</p>
      </div>

      {/* Cache Stats */}
      {cacheStats && (
        <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Cache Status:</strong>{' '}
              {cacheStats.isInitialized ? (
                <span className="admin-badge admin-badge-success">Active</span>
              ) : (
                <span className="admin-badge admin-badge-danger">Not Initialized</span>
              )}
              {' • '}
              <span style={{ color: '#6b7280' }}>
                {cacheStats.size} settings cached
                {cacheStats.lastRefresh && ` • Last refresh: ${new Date(cacheStats.lastRefresh).toLocaleTimeString()}`}
              </span>
            </div>
            <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={handleRefreshCache}>
              🔄 Refresh Cache
            </button>
          </div>
        </div>
      )}

      {/* Filters & Actions */}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`admin-btn admin-btn-sm ${selectedCategory === cat ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat === 'all' ? 'All' : cat.replace('_', ' ')}
              </button>
            ))}
          </div>
          <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
            + New Setting
          </button>
        </div>

        {error && <p style={{ color: '#dc2626' }}>{error}</p>}

        {/* Settings Table */}
        <table className="admin-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Value</th>
              <th>Type</th>
              <th>Category</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {settings.map(setting => (
              <tr key={setting.key}>
                <td>
                  <strong>{setting.key}</strong>
                  {setting.description && (
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {setting.description}
                    </div>
                  )}
                </td>
                <td>
                  <code style={{ 
                    background: '#f3f4f6', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    {formatValue(setting)}
                  </code>
                </td>
                <td>{setting.type}</td>
                <td>
                  <span className={`admin-badge ${getCategoryBadge(setting.category)}`}>
                    {setting.category}
                  </span>
                </td>
                <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {new Date(setting.updatedAt).toLocaleDateString()}
                  <div style={{ fontSize: '0.75rem' }}>by {setting.updatedBy}</div>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() => openEditModal(setting)}
                    >
                      Edit
                    </button>
                    <button 
                      className="admin-btn admin-btn-danger admin-btn-sm"
                      onClick={() => handleDelete(setting.key)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {settings.length === 0 && (
          <div className="admin-empty-state">
            <p>No settings found in this category</p>
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingSetting ? 'Edit Setting' : 'New Setting'}</h3>
              <button className="admin-modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div className="admin-form-group">
              <label>Key</label>
              <input
                type="text"
                value={formData.key}
                onChange={e => setFormData({ ...formData, key: e.target.value })}
                placeholder="SETTING_KEY"
                disabled={!!editingSetting}
              />
            </div>

            <div className="admin-form-group">
              <label>Type</label>
              <select
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value })}
              >
                {TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="admin-form-group">
              <label>Value</label>
              {formData.type === 'boolean' ? (
                <select
                  value={String(formData.value)}
                  onChange={e => setFormData({ ...formData, value: e.target.value })}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : formData.type === 'json' ? (
                <textarea
                  value={formData.value}
                  onChange={e => setFormData({ ...formData, value: e.target.value })}
                  rows={6}
                  style={{ fontFamily: 'monospace' }}
                />
              ) : (
                <input
                  type={formData.type === 'number' ? 'number' : 'text'}
                  value={formData.value}
                  onChange={e => setFormData({ ...formData, value: e.target.value })}
                />
              )}
            </div>

            <div className="admin-form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
              >
                {CATEGORIES.filter(c => c !== 'all').map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="admin-form-group">
              <label>Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="What this setting controls..."
              />
            </div>

            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-secondary" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button className="admin-btn admin-btn-primary" onClick={handleSave}>
                {editingSetting ? 'Save Changes' : 'Create Setting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
